import { message } from 'antd';
import ReportStore from '@/store/Report/ReportStore';
import { UploadEditedReportPayloadDto } from './types';
import { viewOrDownloadFile } from '@/utils/downloadAndViewUtils';

export const VerticalThreeDots = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <circle cx="8" cy="4" r="1.5" />
    <circle cx="8" cy="8" r="1.5" />
    <circle cx="8" cy="12" r="1.5" />
  </svg>
);

interface ReportRecord {
  id?: string;
  title?: string;
  reportDetails?: string;
  status?: string;
  version?: string;
  [key: string]: unknown;
}

export const handlePublishClick = (
  record: ReportRecord,
  onPublishClick?: (record: ReportRecord) => void
) => {
  if (onPublishClick) {
    onPublishClick(record);
  }
};

export const handleEditClick = async (
  record: ReportRecord,
  reportStore: ReportStore,
  t: (key: string) => string
) => {
  if (!reportStore || !record.id) return;

  try {
    const reportData = await reportStore.downloadReport(record.id, record?.version);
    if (!reportData?.assetIdKey) {
      message.error(t('failedToDownloadReportFile'));
      return;
    }

    const fileName = record.reportDetails || record.title || 'report';

    await viewOrDownloadFile({
      url: reportData.assetIdKey,
      fileName,
      t,
    });
  } catch (error: unknown) {
    console.error('Error in handleEditClick:', error);
  }
};

export const handleMenuClick = async (
  key: string,
  record: ReportRecord,
  reportStore: ReportStore,
  t: (key: string) => string,
  onRefreshReports?: () => void,
  onDeleteClick?: (record: ReportRecord) => void
) => {
  if (!reportStore || !record.id) return;

  try {
    if (key === 'Delete') {
      if (onDeleteClick) {
        onDeleteClick(record);
      }
    } else if (key === 'Upload') {
      
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.pptx,.ppt'; 
      fileInput.style.display = 'none';
      
      let isFileSelected = false;
      
      fileInput.onchange = async (event: Event) => {
        isFileSelected = true;
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0];
        
        if (!file) {
          if (fileInput.parentNode) {
            document.body.removeChild(fileInput);
          }
          return;
        }
        
        const validExtensions = ['.pptx', '.ppt'];
        const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
        if (!validExtensions.includes(fileExtension)) {
          message.error(t('onlyPowerPointFilesAllowed'));
          if (fileInput.parentNode) {
            document.body.removeChild(fileInput);
          }
          return;
        }
        
        try {
          const payload: UploadEditedReportPayloadDto = {
            userName: reportStore.rootStore?.userStore?.user?.firstName + ' ' + reportStore.rootStore?.userStore?.user?.lastName,
            reportName: file.name,
            s3Key: '',
          };
          await reportStore.uploadEditedReport(record.id, file, payload);
          message.success(t('reportUploadedSuccessfully'));
          if (onRefreshReports) {
            onRefreshReports();
          }
        } catch (error: unknown) {
          console.error('Error uploading report:', error);
          const errorMessage = error instanceof Error ? error.message : '';
          message.error(t('failedToUploadReport') + (errorMessage ? `: ${errorMessage}` : ''));
        } finally {
          if (fileInput.parentNode) {
            document.body.removeChild(fileInput);
          }
        }
      };
      
      const cleanup = () => {
        setTimeout(() => {
          if (!isFileSelected && fileInput.parentNode) {
            document.body.removeChild(fileInput);
            window.removeEventListener('focus', cleanup);
          }
        }, 300);
      };
      
      window.addEventListener('focus', cleanup);
      
      document.body.appendChild(fileInput);
      fileInput.click();
    }
  } catch (error) {
    message.error(t('anErrorOccurred'));  
  }
};

